import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, ShieldCheck, HardHat, MapPin, Building2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const BBS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/hm9k398i_Screenshot%202019-07-29%20at%201.08.27%20PM.png";
const OPSIQ_LOCKUP = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/m4d6gaj0_ops-iq-lockup-1200.png";

const HERO_IMAGES = {
  main: "https://images.pexels.com/photos/11861957/pexels-photo-11861957.jpeg?auto=compress&cs=tinysrgb&w=1200",
  tl: "https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800",
  tr: "https://images.pexels.com/photos/2760243/pexels-photo-2760243.jpeg?auto=compress&cs=tinysrgb&w=800",
  bl: "https://images.pexels.com/photos/236722/pexels-photo-236722.jpeg?auto=compress&cs=tinysrgb&w=1000",
  br: "https://images.pexels.com/photos/1108117/pexels-photo-1108117.jpeg?auto=compress&cs=tinysrgb&w=1000",
};

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  // Role-based landing: guards jump straight to CheckPoint since they have no
  // Ops-IQ dashboard capabilities. All other roles keep the current behaviour.
  const landingFor = (u) => (u?.role === "guard" ? "/checkpoint" : "/");

  useEffect(() => {
    if (user && user !== null) nav(loc.state?.from?.pathname || landingFor(user), { replace: true });
  }, [user, nav, loc]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back", { description: `Signed in as ${email}` });
      // Redirect is handled by the useEffect above once AuthContext sets `user`.
    } else {
      toast.error("Sign-in failed", { description: res.error });
    }
  };

  const today = new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-md">
          {/* BBS Logo + Ops IQ Lockup */}
          <div className="flex flex-col items-start gap-4">
            <div className="inline-flex items-center" data-testid="bbs-logo">
              <img
                src={BBS_LOGO}
                alt="BBS Global Pvt. Ltd."
                className="h-10 w-auto object-contain select-none pointer-events-none"
                draggable="false"
              />
            </div>
            <div className="inline-flex items-center" data-testid="opsiq-lockup">
              <img
                src={OPSIQ_LOCKUP}
                alt="Ops IQ · Shop Floor · Planning · Output"
                className="h-11 w-auto object-contain select-none pointer-events-none"
                draggable="false"
              />
            </div>
          </div>

          <div className="mt-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fcf0f3] text-[#7C1D3A] text-xs font-semibold tracking-wider uppercase mb-4">
              <ShieldCheck className="w-3 h-3" /> Secure Access
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">
              Welcome to <span className="text-[#7C1D3A]">OPS IQ</span>
            </h1>
            <p className="mt-2 text-slate-500 text-sm leading-relaxed">
              Internal operations portal for <span className="font-semibold text-slate-700">BBS Global Pvt. Ltd.</span>&nbsp;Sign in with your company credentials to continue.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium leading-none text-slate-700" htmlFor="email">Work Email</label>
              <input
                id="email"
                type="email"
                data-testid="login-email-input"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@bbsglobal.in"
                className="flex w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#7C1D3A] md:text-sm mt-1.5 h-11"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none text-slate-700" htmlFor="password">Password</label>
                <a href="#" data-testid="forgot-password-link" className="text-xs font-medium text-[#7C1D3A] hover:underline">Forgot password?</a>
              </div>
              <div className="relative mt-1.5">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#7C1D3A] md:text-sm h-11 pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password-btn"
                  aria-label="Toggle password"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 focus:outline-none focus:text-[#7C1D3A]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#7C1D3A] disabled:pointer-events-none disabled:opacity-60 shadow px-4 py-2 w-full h-11 bg-[#7C1D3A] hover:bg-[#6c1c36] text-white font-medium"
            >
              {busy ? "Signing in…" : "Sign In to OPS IQ"}
            </button>
          </form>
        </div>
      </div>

      {/* Right — Hero photo collage (Asset IQ style) */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#3b0b1a]">
        {/* Photo collage grid */}
        <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 gap-1 opacity-60">
          <div className="col-span-4 row-span-4 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGES.main})` }} />
          <div className="col-span-2 row-span-2 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGES.tl})` }} />
          <div className="col-span-2 row-span-2 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGES.tr})` }} />
          <div className="col-span-3 row-span-2 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGES.bl})` }} />
          <div className="col-span-3 row-span-2 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGES.br})` }} />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3b0b1a]/95 via-[#3b0b1a]/85 to-[#7C1D3A]/70" />

        {/* Grid lines */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 bg-white/70" />
              <span className="font-heading text-xs tracking-[0.3em] uppercase font-bold">Internal Use · BBS Global</span>
            </div>
            <div className="text-right text-[10px] font-mono opacity-70 leading-tight">
              <div>{today}</div>
              <div>IST · Restricted Access</div>
            </div>
          </div>

          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/25 bg-black/20 backdrop-blur-sm text-[11px] font-semibold tracking-widest uppercase mb-6">
              <HardHat className="w-3.5 h-3.5" /> Plant Operations
            </div>
            <h2 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
              BBS Global's unified operations control room.
            </h2>
            <p className="mt-5 text-white/80 text-base leading-relaxed">
              Enquiry costing from Gmail, versioned operation bulletins, and line-wise attendance & output tracking — one login, one system, one source of truth.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-2 max-w-md">
              <div className="flex items-center gap-2 text-[12px] text-white/85 border border-white/15 bg-black/15 backdrop-blur-sm rounded-sm px-3 py-2">
                <MapPin className="w-3.5 h-3.5 shrink-0" /> Multi-plant · Multi-location
              </div>
              <div className="flex items-center gap-2 text-[12px] text-white/85 border border-white/15 bg-black/15 backdrop-blur-sm rounded-sm px-3 py-2">
                <HardHat className="w-3.5 h-3.5 shrink-0" /> CAD · Production · Merchandising
              </div>
              <div className="flex items-center gap-2 text-[12px] text-white/85 border border-white/15 bg-black/15 backdrop-blur-sm rounded-sm px-3 py-2">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> Role-scoped visibility
              </div>
              <div className="flex items-center gap-2 text-[12px] text-white/85 border border-white/15 bg-black/15 backdrop-blur-sm rounded-sm px-3 py-2">
                <Building2 className="w-3.5 h-3.5 shrink-0" /> Registered under BBS Global Pvt. Ltd.
              </div>
            </div>
          </div>

          <div className="flex items-end justify-end pt-6 border-t border-white/15">
            <div className="text-right font-mono text-[10px] text-white/60">
              © BBS Global Pvt. Ltd.<br />
              OPS IQ · Internal Use Only
            </div>
          </div>
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
